<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button, Input, Select, Textarea } from '@drama/ui'
import FormField from '../NovelFormField.vue'
import NovelIssueNote from '../NovelIssueNote.vue'
import { newSceneTemplate } from '@drama/novel-authoring'
import { numberValue } from '@drama/novel-authoring'
import { useSectionModel } from './section-model.js'
import './novel-sections.css'

const PHASES = ['morning', 'afternoon', 'evening', 'night']

const props = defineProps({
  modelValue: { type: Object, required: true },
  issues: { type: Array, default: () => [] },
  upload: { type: Function, default: null },
  fields: { type: String, default: 'all' },
  guided: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'blocked'])
const { t } = useI18n()
const { bind, invalid, notes, removeAt, toggle, write, error } = useSectionModel(props, emit)
const narrative = computed(() => props.modelValue.narrative ?? null)
const templates = computed(() => props.modelValue.scene_templates ?? [])
const people = computed(() => props.modelValue.characters ?? [])
const personIds = computed(() => people.value.map(person => person.id))
const placeOptions = computed(() => (props.modelValue.locations ?? []).map(place => ({ value: place.id, label: place.name || place.id })))
const phaseOptions = computed(() => PHASES.map(phase => ({ value: phase, label: t(`novelStudio.phases.${phase}`) })))

function addTruth() {
  write(['narrative', 'author_truths'], [...(narrative.value.author_truths ?? []), ''])
}

function addSceneTemplate() {
  write(['scene_templates'], [...templates.value, newSceneTemplate(templates.value, props.modelValue)])
}
</script>

<template>
  <section
    class="novel-entities"
    data-testid="novel-narrative-section"
  >
    <p class="novel-entities__hint">
      {{ t('novelStudio.fields.authoringHint') }}
    </p>
    <NovelIssueNote :notes="notes(['narrative'])" />

    <section
      v-if="narrative"
      class="novel-entity"
      data-path="narrative"
      data-testid="novel-narrative-mode"
    >
      <div class="novel-entity__nested-header">
        <h4>{{ t('novelStudio.fields.narrativeMode') }}</h4>
      </div>
      <FormField
        :error="error(['narrative', 'creative_basis'])"
        v-if="fields !== 'advanced'"
        required
        :label="t('novelStudio.fields.creativeBasis')"
        :hint="t('novelStudio.fields.creativeBasisHint')"
      >
        <Textarea
          v-bind="bind(['narrative', 'creative_basis'])"
          :rows="5"
        />
      </FormField>
      <div
        v-if="fields !== 'advanced'"
        class="novel-entity__grid"
      >
        <FormField
          :error="error(['narrative', 'initial_time', 'day'])"
          v-if="!guided || narrative.initial_time?.day !== 0"
          required
          :label="t('novelStudio.fields.storyDay')"
        >
          <Input
            type="number"
            min="0"
            :model-value="narrative.initial_time?.day ?? ''"
            :invalid="invalid(['narrative', 'initial_time', 'day'])"
            data-path="narrative.initial_time.day"
            @update:model-value="write(['narrative', 'initial_time', 'day'], numberValue($event))"
          />
        </FormField>
        <FormField
          :error="error(['narrative', 'initial_time', 'phase'])"
          required
          :label="t('novelStudio.fields.storyPhase')"
        >
          <Select
            :model-value="narrative.initial_time?.phase ?? ''"
            :options="phaseOptions"
            :aria-label="t('novelStudio.fields.storyPhase')"
            :invalid="invalid(['narrative', 'initial_time', 'phase'])"
            data-path="narrative.initial_time.phase"
            block
            @update:model-value="write(['narrative', 'initial_time', 'phase'], $event)"
          />
        </FormField>
      </div>
      <div
        v-if="fields !== 'basic'"
        class="novel-entity__nested"
        data-path="narrative.author_truths"
      >
        <div class="novel-entity__nested-header">
          <h4>{{ t('novelStudio.requirements.optionalLabel', { label: t('novelStudio.fields.authorTruths') }) }}</h4>
          <Button
            variant="secondary"
            size="sm"
            data-testid="novel-add-author-truth"
            @click="addTruth"
          >
            {{ t('novelStudio.fields.addEntry') }}
          </Button>
        </div>
        <div
          class="novel-entity__help"
          data-testid="novel-author-truths-help"
        >
          <p>{{ t('novelStudio.fields.authorTruthsHint') }}</p>
          <details>
            <summary>{{ t('novelStudio.fields.writingExampleLabel') }}</summary>
            <p>{{ t('novelStudio.fields.authorTruthsExample') }}</p>
          </details>
        </div>
        <div
          v-for="(_, truthIndex) in narrative.author_truths || []"
          :key="truthIndex"
          class="novel-entity__inline"
        >
          <FormField
            :error="error(['narrative', 'author_truths', truthIndex])"
            required
            :label="`${t('novelStudio.fields.authorTruths')} #${truthIndex + 1}`"
          >
            <Textarea
              v-bind="bind(['narrative', 'author_truths', truthIndex])"
              :rows="2"
              :aria-label="`${t('novelStudio.fields.authorTruths')} #${truthIndex + 1}`"
            />
          </FormField>
          <Button
            variant="secondary"
            size="sm"
            :aria-label="t('novelStudio.fields.removeNamed', { name: `${t('novelStudio.fields.authorTruths')} #${truthIndex + 1}` })"
            @click="removeAt(['narrative', 'author_truths', truthIndex])"
          >
            ×
          </Button>
        </div>
      </div>
    </section>

    <template v-if="fields !== 'basic'">
      <header class="novel-entity__nested-header">
        <h4>{{ t('novelStudio.requirements.optionalLabel', { label: t('novelStudio.fields.sceneTemplates') }) }} · {{ templates.length }}</h4>
        <Button
          variant="secondary"
          size="sm"
          data-testid="novel-add-scene-template"
          @click="addSceneTemplate"
        >
          {{ t('novelStudio.fields.addSceneTemplate') }}
        </Button>
      </header>
      <p class="novel-entities__hint">
        {{ t('novelStudio.fields.sceneTemplatesHint') }}
      </p>
      <p
        v-if="!templates.length"
        class="novel-entities__empty"
      >
        {{ t('novelStudio.empty.sceneTemplates') }}
      </p>
      <article
        v-for="(scene, index) in templates"
        :key="`${scene.id}-${index}`"
        class="novel-entity"
        :class="{ 'has-error': invalid(['scene_templates', index]) }"
        :data-path="`scene_templates.${index}`"
        tabindex="-1"
        :data-testid="`novel-scene-template-${index}`"
      >
        <header class="novel-entity__header">
          <div class="novel-entity__title">
            <code>{{ scene.id }}</code>
          </div>
          <Button
            variant="secondary"
            size="sm"
            :aria-label="t('novelStudio.fields.removeNamed', { name: scene.id })"
            @click="removeAt(['scene_templates', index])"
          >
            {{ t('novelStudio.fields.remove') }}
          </Button>
        </header>
        <NovelIssueNote :notes="notes(['scene_templates', index])" />
        <FormField
          :error="error(['scene_templates', index, 'location_id'])"
          required
          :label="t('novelStudio.fields.location')"
        >
          <Select
            :model-value="scene.location_id ?? ''"
            :options="placeOptions"
            :aria-label="t('novelStudio.fields.location')"
            :invalid="invalid(['scene_templates', index, 'location_id'])"
            :data-path="`scene_templates.${index}.location_id`"
            block
            @update:model-value="write(['scene_templates', index, 'location_id'], $event)"
          />
        </FormField>
        <fieldset
          class="novel-entity__checks"
          :data-path="`scene_templates.${index}.present_ids`"
        >
          <legend>{{ t('novelStudio.requirements.optionalLabel', { label: t('novelStudio.fields.presentCharacters') }) }}</legend>
          <label
            v-for="person in people"
            :key="person.id"
            class="novel-entity__check"
          >
            <input
              type="checkbox"
              :checked="(scene.present_ids || []).includes(person.id)"
              @change="toggle(['scene_templates', index, 'present_ids'], person.id, $event.target.checked, personIds)"
            >
            {{ person.name || person.id }}
          </label>
        </fieldset>
        <FormField
          :error="error(['scene_templates', index, 'situation'])"
          required
          :label="t('novelStudio.fields.situation')"
        >
          <Textarea
            v-bind="bind(['scene_templates', index, 'situation'])"
            :rows="3"
          />
        </FormField>
        <FormField
          :error="error(['scene_templates', index, 'direction'])"
          required
          :label="t('novelStudio.fields.direction')"
        >
          <Textarea
            v-bind="bind(['scene_templates', index, 'direction'])"
            :rows="3"
          />
        </FormField>
      </article>
    </template>
  </section>
</template>
